<template>
  <div class="design-view">
    <div class="page-header">
      <h2>디자인 관리</h2>
      <div class="header-actions">
        <el-button type="primary" @click="openDialog('add')">+ 디자인 추가</el-button>
        <el-button @click="exportExcel">엑셀 다운로드</el-button>
        <el-button @click="openExcelDialog">엑셀 업로드</el-button>
        <el-button @click="showHelp" size="small">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 스마트뷰 탭 — MD 일상 동선 (전체 / 내 담당 / 액션 필요) -->
    <el-tabs v-model="activeView" class="view-tabs" @tab-change="fetchDesigns">
      <el-tab-pane label="전체" name="all" />
      <el-tab-pane label="내 담당" name="mine" />
      <el-tab-pane label="액션 필요" name="action" />
    </el-tabs>

    <!-- 필터 -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" :model="filters" size="default">
        <el-form-item label="년도">
          <el-select v-model="filters.year" clearable placeholder="전체" style="width: 100px"
                     @change="fetchDesigns">
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y" />
          </el-select>
        </el-form-item>
        <el-form-item label="시즌">
          <el-select v-model="filters.season" clearable placeholder="전체" style="width: 90px"
                     @change="fetchDesigns">
            <el-option label="SS" value="SS" />
            <el-option label="FW" value="FW" />
          </el-select>
        </el-form-item>
        <el-form-item label="브랜드">
          <el-select v-model="filters.brand" filterable clearable placeholder="전체" style="width: 140px"
                     @change="fetchDesigns">
            <el-option v-for="b in codeStore.brands" :key="b.id" :label="b.name" :value="b.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="샘플상태">
          <el-select v-model="filters.sample_status" clearable placeholder="전체" style="width: 110px"
                     @change="fetchDesigns">
            <el-option label="요청" value="requested" />
            <el-option label="완료" value="completed" />
            <el-option label="검토 중" value="reviewing" />
          </el-select>
        </el-form-item>
        <el-form-item label="구분">
          <el-select v-model="filters.sample_main_type" clearable placeholder="전체" style="width: 110px"
                     @change="fetchDesigns">
            <el-option label="샘플" value="SAMPLE" />
            <el-option label="메인" value="MAIN" />
            <el-option label="샘플+메인" value="BOTH" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-input v-model="filters.search" placeholder="스타일번호, 품명, 브랜드 검색"
                    clearable style="width: 240px"
                    @keyup.enter="fetchDesigns" @clear="fetchDesigns" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchDesigns">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 그리드 -->
    <div class="grid-container">
      <ag-grid-vue
        class="ag-theme-alpine"
        style="width: 100%; height: 100%"
        :rowData="designs"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :pagination="true"
        :paginationPageSize="20"
        :rowHeight="40"
        rowSelection="single"
        @rowClicked="onRowClicked"
        @gridReady="onGridReady"
      />
    </div>

    <DesignFormModal ref="designModal" @saved="onDesignSaved" />
    <!-- 엑셀 업로드 다이얼로그 (검증 → 반영, 2026-07-17) -->
    <el-dialog v-model="excelDialogVisible" title="디자인(스타일) 엑셀 업로드" width="620px"
               :close-on-click-modal="false" destroy-on-close>
      <div style="font-size:12px; color:#909399; margin-bottom:6px">
        시즌 초 스타일 일괄 등록용입니다. <el-button size="small" text type="primary"
        @click="downloadServerExcel" :loading="excelDownloading">서식(현재 목록) 다운로드</el-button><br/>
        고객사 스타일번호가 키 — 이미 있으면 빈 항목만 채우고, 없으면 신규 등록.
        기존 값 변경은 ID를 적은 행만 허용됩니다. 내부번호·품명은 자동 생성.
      </div>
      <input type="file" accept=".xlsx" @change="onExcelFileChange" style="margin: 10px 0;" />
      <template v-if="excelR">
        <el-alert :type="excelAlertType" :closable="false" show-icon
                  :title="excelReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
          <div>신규 {{ excelR.created.length }} · 보강 {{ excelR.updated.length }} ·
            변경없음 {{ excelR.unchanged || 0 }} · 변경후보 {{ excelChanges.length }} ·
            경고 {{ excelWarnings.length }} · 오류 {{ excelR.errors.length }}</div>
        </el-alert>

        <div v-for="(err, i) in excelR.errors" :key="'e' + i" class="xl-line xl-line--error">
          ✖ {{ err }}</div>
        <div v-if="excelReportHasError" class="xl-line xl-line--error">
          오류가 있는 동안에는 반영할 수 없습니다 — 엑셀을 고친 뒤 다시 검증하세요.</div>

        <template v-if="excelChanges.length">
          <div class="xl-head">
            값이 달라 반영하지 않은 항목입니다 (확인 후 반영)
            <el-button size="small" text type="primary" :loading="excelChangesLoading"
                       @click="downloadChanges">변경후보 서식 받기</el-button>
          </div>
          <div v-for="(c, i) in excelChanges.slice(0, 20)" :key="'c' + i" class="xl-line">
            · {{ c.style }} — {{ c.summary }}</div>
          <div v-if="excelChanges.length > 20" class="xl-line">
            … 외 {{ excelChanges.length - 20 }}건 (서식으로 받아 확인하세요)</div>
        </template>

        <div v-for="(wn, i) in excelWarnings.slice(0, 20)" :key="'w' + i" class="xl-line xl-line--warn">
          ⚠ {{ wn }}</div>
        <div v-if="excelWarnings.length > 20" class="xl-line xl-line--warn">
          … 외 {{ excelWarnings.length - 20 }}건 (경고는 반영을 막지 않습니다)</div>

        <div v-if="excelR.created.length" class="xl-line xl-line--wrap">
          신규: {{ excelR.created.join(', ') }}</div>
        <div v-if="excelR.updated.length" class="xl-line xl-line--wrap">
          보강: {{ excelR.updated.join(', ') }}</div>
      </template>
      <template #footer>
        <el-button @click="excelDialogVisible = false">닫기</el-button>
        <el-button type="primary" plain :disabled="!excelFile" :loading="excelUploading"
                   @click="uploadExcel(false)">검증</el-button>
        <el-button type="primary"
                   :disabled="!excelFile || !excelReport || !excelReport.dry_run || excelReportHasError"
                   :loading="excelUploading" @click="uploadExcel(true)">반영</el-button>
      </template>
    </el-dialog>


    <HelpPanel v-model="helpVisible" :content-path="'screens/design-list'" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { AgGridVue } from 'ag-grid-vue3'
import { QuestionFilled } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import { ElMessage, ElMessageBox } from 'element-plus'
import DesignFormModal from '@/components/DesignFormModal.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const router = useRouter()

const codeStore = useCodeStore()
const designs = ref([])
const designModal = ref(null)
const gridApi = ref(null)
const loading = ref(false)
const helpVisible = ref(false)
const showHelp = () => { helpVisible.value = true }

const activeView = ref('all') // 스마트뷰: all | mine | action

const filters = ref({
  year: new Date().getFullYear(),
  season: '',
  brand: '',
  sample_status: '',
  sample_main_type: '',
  search: '',
})

// 미래 4년 ~ 과거 5년 (2026-11-04 제보 이미영: 12월부터 2027 S/S 샘플 진행 — 미래 년도 필요, 2030까지)
const yearOptions = computed(() => {
  const current = new Date().getFullYear()
  return Array.from({ length: 10 }, (_, i) => current + 4 - i)
})

const columnDefs = ref([
  { field: 'main_style_number', headerName: '고객사 스타일번호', width: 150, pinned: 'left', sortable: true,
    cellStyle: { fontWeight: '600' } },
  { field: 'style_number', headerName: '내부번호', width: 140, sortable: true },
  { field: 'brand_name', headerName: '브랜드', width: 120, sortable: true },
  { field: 'designer_name', headerName: '담당MD', width: 100, sortable: true },
  { field: 'product_name', headerName: '품명', width: 160, sortable: true },
  {
    headerName: '년도/시즌', width: 100, sortable: true,
    valueGetter: (params) => params.data ? `${params.data.year} ${params.data.season_display}` : '',
  },
  {
    headerName: '이미지', width: 70,
    cellRenderer: (params) => {
      if (params.data && params.data.front_image_url) {
        return `<img src="${params.data.front_image_url}" style="width:32px;height:32px;object-fit:cover;border-radius:4px" />`
      }
      return '<span style="color:#ccc">-</span>'
    },
  },
  {
    field: 'sample_status_display', headerName: '샘플상태', width: 100,
    cellRenderer: (params) => {
      if (!params.value) return ''
      const colors = { '요청': '#E6A23C', '완료': '#67C23A', '검토 중': '#409EFF' }
      const color = colors[params.value] || '#909399'
      return `<span style="padding:2px 8px;border-radius:4px;font-size:12px;color:white;background:${color}">${params.value}</span>`
    },
  },
  {
    field: 'approval_status_display', headerName: '승인', width: 80,
    cellRenderer: (params) => {
      if (!params.value) return ''
      const colors = { '대기': '#909399', '승인': '#67C23A', '반려': '#F56C6C' }
      const color = colors[params.value] || '#909399'
      return `<span style="padding:2px 6px;border-radius:4px;font-size:12px;color:white;background:${color}">${params.value}</span>`
    },
  },
  { field: 'sample_main_type_display', headerName: '구분', width: 90, sortable: true },
  { field: 'request_date', headerName: '요청일', width: 110, sortable: true },
  {
    headerName: '진행', width: 130,
    cellRenderer: (params) => {
      if (!params.data?.lifecycle_summary) return ''
      const ls = params.data.lifecycle_summary
      const stages = [
        { done: true, label: '기획' },
        { done: ls.sample_completed > 0, active: ls.sample_count > 0 && ls.sample_completed === 0, label: '샘플' },
        { done: ls.quotation_count > 0 && ls.main_request_count > 0, active: ls.quotation_count > 0, label: '견적' },
        { done: ls.has_delivery, active: ls.main_request_count > 0, label: '생산' },
        { done: ls.has_delivery, label: '납품' },
      ]
      return stages.map(s => {
        const color = s.done ? '#67C23A' : s.active ? '#409EFF' : '#dcdfe6'
        return `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin:0 2px" title="${s.label}"></span>`
      }).join('')
    },
  },
  {
    field: 'parent_style_number', headerName: '원본', width: 100,
    cellRenderer: (params) => {
      if (!params.value) return ''
      return `<span style="color:#909399;font-size:12px">${params.value}</span>`
    },
  },
  {
    headerName: '', width: 50,
    cellRenderer: (params) => {
      if (params.data && params.data.is_locked) {
        return '<span style="color:#E6A23C" title="잠금">&#128274;</span>'
      }
      return ''
    },
  },
  {
    headerName: '', width: 60, pinned: 'right',
    cellRenderer: () => '<button class="copy-btn" style="padding:2px 8px;border:1px solid #dcdfe6;border-radius:4px;background:#fff;cursor:pointer;font-size:12px">복사</button>',
    onCellClicked: (params) => {
      if (params.event.target.classList.contains('copy-btn')) {
        handleDuplicate(params.data)
      }
    },
  },
  {
    field: 'is_auto', headerName: '입력', width: 72, sortable: true,
    cellRenderer: (params) => {
      if (!params.data) return ''
      if (params.value) {
        const o = params.data.auto_origin || {}
        const tip = `자동 변환 — 원본: ${o.source_file || ''}${o.sheet_name ? ' / ' + o.sheet_name : ''}`
        return `<span title="${tip}" style="padding:2px 8px;border-radius:4px;font-size:11px;color:white;background:#1f3a5f">자동</span>`
      }
      return '<span style="color:#909399;font-size:11px">직접</span>'
    },
  },
])

const defaultColDef = {
  resizable: true,
  suppressMovable: true,
}

const onGridReady = (params) => {
  gridApi.value = params.api
}

const fetchDesigns = async () => {
  loading.value = true
  try {
    const params = {}
    if (filters.value.year) params.year = filters.value.year
    if (filters.value.season) params.season = filters.value.season
    if (filters.value.brand) params.brand = filters.value.brand
    if (filters.value.sample_status) params.sample_status = filters.value.sample_status
    if (filters.value.sample_main_type) params.sample_main_type = filters.value.sample_main_type
    if (filters.value.search) params.search = filters.value.search
    if (activeView.value === 'mine') params.mine = 1
    if (activeView.value === 'action') params.action_needed = 1

    const response = await axiosInstance.get('designs/', { params })
    designs.value = response.data.results || response.data || []
  } catch (error) {
    console.error('디자인 조회 실패:', error)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    year: new Date().getFullYear(),
    season: '',
    brand: '',
    sample_status: '',
    sample_main_type: '',
    search: '',
  }
  fetchDesigns()
}

const onRowClicked = (event) => {
  // 복사 버튼 클릭 시 상세 이동 방지
  if (event.event?.target?.classList?.contains('copy-btn')) return
  if (event.data?.id) {
    router.push({ name: 'DesignDetail', params: { id: event.data.id } })
  }
}

const openDialog = (mode, design = null) => {
  if (mode === 'add') {
    designModal.value.open()
  } else if (mode === 'edit') {
    designModal.value.open(design)
  }
}

const onDesignSaved = () => {
  fetchDesigns()
}

const handleDuplicate = async (design) => {
  try {
    await ElMessageBox.confirm(
      `디자인 "${design.style_number}"을(를) 복사하시겠습니까?`,
      '디자인 복사',
      { confirmButtonText: '복사', cancelButtonText: '취소', type: 'info' }
    )
    const res = await axiosInstance.post(`designs/${design.id}/duplicate/`)
    ElMessage.success(`복사 완료: ${res.data.style_number}`)
    fetchDesigns()
  } catch (err) {
    if (err !== 'cancel') {
      console.error('디자인 복사 실패:', err)
      ElMessage.error('디자인 복사 중 오류가 발생했습니다')
    }
  }
}

const exportExcel = async () => {
  try {
    const params = {}
    if (filters.value.year) params.year = filters.value.year
    if (filters.value.season) params.season = filters.value.season
    if (filters.value.brand) params.brand = filters.value.brand
    if (filters.value.sample_status) params.sample_status = filters.value.sample_status
    if (activeView.value === 'mine') params.mine = 1
    if (activeView.value === 'action') params.action_needed = 1

    const res = await axiosInstance.get('designs/export/', {
      params,
      responseType: 'blob',
    })
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `designs_${filters.value.year || 'all'}.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('엑셀 다운로드 실패:', error)
    ElMessage.error('엑셀 다운로드 중 오류가 발생했습니다')
  }
}

// ── 엑셀 업로드 (2026-07-17) — 헤더의 [엑셀 다운로드]는 그리드 export, 서식은 다이얼로그 안 버튼 ──
const excelDownloading = ref(false)
const excelDialogVisible = ref(false)
const excelUploading = ref(false)
const excelFile = ref(null)
const excelReport = ref(null)
const excelChangesLoading = ref(false)
const excelR = computed(() => excelReport.value?.designs || null)
const excelChanges = computed(() => excelR.value?.changes || [])
const excelWarnings = computed(() => excelR.value?.warnings || [])
const excelReportHasError = computed(() => !!excelR.value && excelR.value.errors.length > 0)
const excelAlertType = computed(() => {
  if (excelReportHasError.value) return 'error'
  if (excelChanges.value.length || excelWarnings.value.length) return 'warning'
  return 'success'
})

// 변경후보 서식 — ID가 채워진 업로드 서식. 반영할 행만 남겨 다시 올리면 실제 변경된다.
const downloadChanges = async () => {
  if (!excelFile.value) return
  excelChangesLoading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post('/designs/excel-changes/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `디자인_변경후보_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('변경후보 다운로드 실패:', e)
    ElMessage.error('변경후보 서식을 받지 못했습니다')
  } finally {
    excelChangesLoading.value = false
  }
}

const downloadServerExcel = async () => {
  excelDownloading.value = true
  try {
    const res = await axiosInstance.get('/designs/excel-export/', { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `디자인_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('서식 다운로드 실패:', e)
    ElMessage.error('서식 다운로드에 실패했습니다')
  } finally {
    excelDownloading.value = false
  }
}
const openExcelDialog = () => {
  excelFile.value = null
  excelReport.value = null
  excelDialogVisible.value = true
}
const onExcelFileChange = (e) => {
  excelFile.value = e.target.files?.[0] || null
  excelReport.value = null
}
const uploadExcel = async (apply) => {
  if (!excelFile.value) return
  excelUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post(
      `/designs/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    excelReport.value = res.data
    if (apply) {
      ElMessage.success('반영되었습니다')
      fetchDesigns()
    }
  } catch (e) {
    console.error('엑셀 업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '엑셀 업로드에 실패했습니다')
  } finally {
    excelUploading.value = false
  }
}

onMounted(async () => {
  await Promise.all([
    codeStore.fetchBrands(),
    fetchDesigns(),
  ])
})
</script>

<style scoped>
.design-view {
  padding: 0;
}
/* 엑셀 업로드 리포트 줄 (2026-08-04) */
.xl-head {
  font-size: var(--sw-font-xs);
  color: var(--el-text-color-regular);
  margin-top: var(--sw-space-2);
}
.xl-line {
  font-size: var(--sw-font-xs);
  color: var(--el-text-color-regular);
  margin-top: var(--sw-space-1);
}
.xl-line--error { color: var(--el-color-danger); }
.xl-line--warn { color: var(--el-color-warning); }
.xl-line--wrap { word-break: break-all; }
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.page-header h2 {
  margin: 0;
}
.header-actions {
  display: flex;
  gap: 8px;
}
.filter-card {
  margin-bottom: 12px;
}
.filter-card :deep(.el-card__body) {
  padding: 12px 16px 0;
}
.grid-container {
  margin-top: 0;
}

/* ===== 이중 스크롤 방지 (PC) =====
   고정 calc(100vh - 280px) 가 상단 요소 실제 높이와 어긋나 페이지+그리드 이중 스크롤 유발.
   .content(전역 스크롤러) 가시 영역을 flex 로 채워 그리드 스크롤만 남긴다. (2026-06-11) */
@media (min-width: 769px) {
  .design-view {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .design-view .page-header,
  .design-view .filter-card {
    flex-shrink: 0;
  }
  .grid-container {
    flex: 1;
    min-height: 0;
  }
}

/* 모바일 폴백: flex 미적용이므로 그리드 높이 직접 지정 */
@media (max-width: 768px) {
  .grid-container {
    height: calc(100vh - 280px);
  }
}
</style>
